import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, X } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';
import {
  buildOrgLicenseRows,
  formatDateLabel,
  formatDaysRemaining,
  isoDateInputValue,
  isOrgLicenseEditable,
  isProtectedFoodModule,
} from '../../utils/organizationLicenseSummary';

const ESTADO_OPTIONS = ['activo', 'vencido', 'pendiente', 'pendiente_sede', 'pendiente_pago_online', 'cancelado'];

export default function ManageOrgLicensesModal({
  open,
  organizationName = '',
  operator = null,
  onClose,
  onExtendOperator,
  onSaved,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isSuperAdmin = [user?.rol, ...(user?.roles || [])].includes('super_admin');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [moduleAccess, setModuleAccess] = useState({});
  const [plans, setPlans] = useState([]);
  const [accountForm, setAccountForm] = useState({
    plan: '',
    fecha_inicio: '',
    fecha_vencimiento: '',
    estado: 'activo',
    accountId: null,
  });

  const subscription = operator?.subscription || null;

  const load = useCallback(async () => {
    if (!operator?.id) return;
    setLoading(true);
    try {
      const [licRes, planRes] = await Promise.all([
        api.get(`/licenses/user/${operator.id}`),
        api.get('/accounts/plans', { params: { platform_only: 'true' } }),
      ]);
      const licData = licRes.data?.data || licRes.data || {};
      setLicenses(Array.isArray(licData.licenses) ? licData.licenses : []);
      setModuleAccess(licData.module_access || operator.module_access || {});
      const planRows = Array.isArray(planRes.data) ? planRes.data : (planRes.data?.planes || planRes.data?.plans || []);
      setPlans(planRows.filter((p) => p.activo !== false));
      const sub = operator.subscription || {};
      setAccountForm({
        plan: sub.plan || '',
        fecha_inicio: isoDateInputValue(sub.fecha_inicio),
        fecha_vencimiento: isoDateInputValue(sub.fecha_vencimiento),
        estado: sub.estado || 'activo',
        accountId: sub.account_id || null,
      });
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('orgs.licenses_load_error', 'No se pudieron cargar las licencias'),
      });
    } finally {
      setLoading(false);
    }
  }, [operator, t]);

  useEffect(() => {
    if (open && operator?.id) load();
  }, [open, operator?.id, load]);

  const rows = useMemo(
    () => buildOrgLicenseRows(licenses, subscription),
    [licenses, subscription],
  );

  const saveAccount = async () => {
    if (!accountForm.accountId) return;
    setSaving(true);
    try {
      await api.put(`/accounts/${accountForm.accountId}`, {
        plan: accountForm.plan || undefined,
        fecha_inicio: accountForm.fecha_inicio ? new Date(accountForm.fecha_inicio).toISOString() : undefined,
        fecha_vencimiento: accountForm.fecha_vencimiento ? new Date(accountForm.fecha_vencimiento).toISOString() : undefined,
        estado: accountForm.estado,
      });
      emitToast({
        type: 'success',
        title: t('orgs.licenses_saved', 'Licencia actualizada'),
        message: t('orgs.account_vigencia_saved', 'Plan y vigencia de la cuenta admin guardados.'),
      });
      await load();
      onSaved?.();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('orgs.licenses_save_error', 'No se pudo guardar'),
      });
    } finally {
      setSaving(false);
    }
  };

  const saveModuleUntil = async (moduleCode, dateValue) => {
    if (!isSuperAdmin || !operator?.id || !dateValue) return;
    setSaving(true);
    try {
      await api.put(`/licenses/user/${operator.id}`, {
        module_access: moduleAccess,
        license_meta: {
          [moduleCode]: { valid_until: new Date(dateValue).toISOString() },
        },
      });
      emitToast({
        type: 'success',
        title: t('orgs.licenses_saved', 'Licencia actualizada'),
        message: `${moduleCode.toUpperCase()} · ${formatDateLabel(dateValue)}`,
      });
      await load();
      onSaved?.();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('orgs.licenses_save_error', 'No se pudo guardar'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="form-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={() => !saving && onClose?.()}
    >
      <div className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-sky-100 text-sky-800 shrink-0">
              <Layers size={18} />
            </span>
            <div className="min-w-0">
              <h4 className="font-semibold text-stone-900 truncate">
                {t('orgs.manage_licenses', 'Licencias de la organización')}
              </h4>
              <p className="text-xs text-stone-500 truncate">
                {organizationName}
                {operator?.email ? ` · ${operator.email}` : ''}
              </p>
            </div>
          </div>
          <button type="button" className="text-stone-500 hover:text-stone-900" onClick={onClose} disabled={saving}>
            <X size={20} />
          </button>
        </div>

        <div className="form-modal-content space-y-4">
          <p className="text-sm text-stone-600">
            {t('orgs.licenses_hint', 'Una organización puede tener varias licencias activas (FOOD, Training). FOOD PLAN se muestra en solo lectura; su operación interna no se modifica desde aquí.')}
          </p>

          {loading ? (
            <p className="text-stone-500 text-sm">{t('common.loading', 'Cargando...')}</p>
          ) : (
            <>
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('orgs.licenses', 'Licencia')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.plan', 'Plan')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('vigencias.valid_until', 'Vigencia')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.status', 'Estado')}</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">{t('common.actions', 'Acciones')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-stone-500">
                          {t('orgs.no_licenses', 'Sin licencias activas registradas.')}
                        </td>
                      </tr>
                    ) : rows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-3 py-2 font-medium">
                          {row.label}
                          {isProtectedFoodModule(row.moduleCode) ? (
                            <span className="ml-1 text-[10px] text-stone-400 uppercase">{t('orgs.read_only', 'solo lectura')}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-xs">{row.plan || '—'}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          {row.validUntil
                            ? `${formatDateLabel(row.validUntil)} · ${formatDaysRemaining(row.daysLeft)}`
                            : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${row.active ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>
                            {row.estado || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {row.moduleCode !== 'admin' && isOrgLicenseEditable(row.moduleCode) && onExtendOperator ? (
                            <button
                              type="button"
                              className="btn-secondary text-xs py-1 px-2"
                              disabled={saving}
                              onClick={() => onExtendOperator({
                                userId: operator.id,
                                moduleCode: row.moduleCode,
                                userName: operator.nombre,
                                planName: row.plan,
                              })}
                            >
                              {t('vigencias.extend_short', 'Ampliar')}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {subscription?.has_account && accountForm.accountId ? (
                <section className="ui-light-panel p-4 rounded-xl border border-stone-200 space-y-3">
                  <h5 className="font-semibold text-stone-900 text-sm">
                    {t('orgs.edit_admin_account', 'Cuenta administrador — plan y vigencia')}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block sm:col-span-2">
                      <span className="text-xs font-medium text-stone-600">{t('common.plan', 'Plan')}</span>
                      <select
                        className="input mt-1 w-full text-sm"
                        value={accountForm.plan}
                        disabled={saving}
                        onChange={(e) => setAccountForm((p) => ({ ...p, plan: e.target.value }))}
                      >
                        <option value="">{t('common.none', 'Ninguno')}</option>
                        {plans.map((p) => (
                          <option key={p.nombre} value={p.nombre}>{p.nombre}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-stone-600">{t('vigencias.valid_from', 'Fecha inicio')}</span>
                      <input
                        type="date"
                        className="input mt-1 w-full text-sm"
                        value={accountForm.fecha_inicio}
                        disabled={saving}
                        onChange={(e) => setAccountForm((p) => ({ ...p, fecha_inicio: e.target.value }))}
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-stone-600">{t('vigencias.valid_until', 'Fecha vencimiento')}</span>
                      <input
                        type="date"
                        className="input mt-1 w-full text-sm"
                        value={accountForm.fecha_vencimiento}
                        disabled={saving}
                        onChange={(e) => setAccountForm((p) => ({ ...p, fecha_vencimiento: e.target.value }))}
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs font-medium text-stone-600">{t('common.status', 'Estado')}</span>
                      <select
                        className="input mt-1 w-full text-sm"
                        value={accountForm.estado}
                        disabled={saving}
                        onChange={(e) => setAccountForm((p) => ({ ...p, estado: e.target.value }))}
                      >
                        {ESTADO_OPTIONS.map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <button type="button" className="btn-primary text-sm" disabled={saving} onClick={saveAccount}>
                    {saving ? t('common.saving', 'Guardando...') : t('orgs.save_account_vigencia', 'Guardar plan y vigencia')}
                  </button>
                </section>
              ) : null}

              {isSuperAdmin && rows.some((r) => r.moduleCode !== 'admin' && isOrgLicenseEditable(r.moduleCode)) ? (
                <section className="space-y-2">
                  <h5 className="font-semibold text-stone-900 text-sm">
                    {t('orgs.edit_module_vigencia', 'Vigencia por módulo (super admin)')}
                  </h5>
                  {rows.filter((r) => r.moduleCode !== 'admin' && isOrgLicenseEditable(r.moduleCode)).map((row) => (
                    <div key={`edit-${row.id}`} className="flex flex-wrap items-end gap-2">
                      <span className="text-sm font-medium text-stone-700 w-24">{row.label}</span>
                      <input
                        type="date"
                        className="input text-sm py-1"
                        defaultValue={isoDateInputValue(row.validUntil)}
                        disabled={saving}
                        id={`module-until-${row.moduleCode}`}
                      />
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        disabled={saving}
                        onClick={() => {
                          const el = document.getElementById(`module-until-${row.moduleCode}`);
                          saveModuleUntil(row.moduleCode, el?.value);
                        }}
                      >
                        {t('common.save', 'Guardar')}
                      </button>
                    </div>
                  ))}
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
